import apiClient from './apiClient';

/**
 * Per-user accessibility preferences. Stored server-side under
 * `users.preferences.accessibilityPrefs` so a doctor's "Large text +
 * high contrast + dark mode + RTL override" pick follows them between
 * desktop, iPad, and the PWA without re-configuring on each device.
 *
 * Frontend role mirrors voicePrefsApi:
 *   - Post-auth boot: GET, merge into useAccessibilityStore so a new
 *     device inherits the saved settings.
 *   - On change: PUT (debounced) so other devices see the change next
 *     time they boot.
 *
 * Backend default ("do nothing different") mirrors the store defaults:
 * normal text, normal contrast, motion on, system colour scheme,
 * direction follows the active language. This means existing users see
 * no visible change until they opt in.
 */

export type TextScale = 'normal' | 'large' | 'xlarge' | 'xxlarge';
export type ContrastMode = 'normal' | 'high';
export type ColorSchemePref = 'system' | 'light' | 'dark';
export type DirectionPref = 'auto' | 'ltr' | 'rtl';

export interface AccessibilityPrefsDTO {
  textScale: TextScale;
  contrast: ContrastMode;
  reduceMotion: boolean;
  colorScheme: ColorSchemePref;
  direction: DirectionPref;
}

const BASE = '/users/me/accessibility-prefs';

export const accessibilityPrefsApi = {
  async get(): Promise<AccessibilityPrefsDTO> {
    const res = await apiClient.get<AccessibilityPrefsDTO>(BASE);
    return res.data;
  },

  async put(
    next: Partial<AccessibilityPrefsDTO>,
  ): Promise<AccessibilityPrefsDTO> {
    const res = await apiClient.put<AccessibilityPrefsDTO>(BASE, next);
    return res.data;
  },
};
